const DIRECT_FILE_PREVIEW_RESOURCE_TYPES = [7, 8, 9]
const DIRECT_FILE_PREVIEW_IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'bmp']
const COMPACT_COUNT_UNIT_LANGUAGES = ['zh', 'tw', 'ja', 'ko']

export default {
  methods: {
    isDirectFilePreviewResourceType(resourceType) {
      const value = Number(resourceType)
      return Number.isFinite(value) && DIRECT_FILE_PREVIEW_RESOURCE_TYPES.includes(value)
    },
    isDirectFilePreviewItem(item) {
      return !!(item && this.isDirectFilePreviewResourceType(this.pickDirectFilePreviewValue(item, ['resourceType', 'resource_type', 'type'])) && this.pickDirectFilePreviewPath(item))
    },
    isDirectFilePreviewDetail(detail) {
      return !!(detail && detail.isDirectFilePreview && this.isDirectFilePreviewResourceType(detail.resourceType) && detail.filePath)
    },
    createDirectFilePreviewDetail(item) {
      if (!this.isDirectFilePreviewItem(item)) {
        return null
      }
      const filePath = this.pickDirectFilePreviewPath(item)
      const previewUrl = this.toDirectFilePreviewUrl(filePath)
      if (!previewUrl) {
        return null
      }
      const extension = this.getDirectFilePreviewExtension(filePath)
      const name = this.pickDirectFilePreviewString(item, ['name', 'title', 'fileName', 'filename', 'resourceName', 'paperName', 'bookName'])
        || this.getDirectFilePreviewFileName(filePath)
        || 'Preview'
      const resourceType = this.pickDirectFilePreviewValue(item, ['resourceType', 'resource_type', 'type'])
      const isImage = DIRECT_FILE_PREVIEW_IMAGE_EXTENSIONS.includes(extension)
      return {
        name,
        resourceType,
        filePath,
        resourceUrl: extension == 'pdf' ? previewUrl : '',
        imagePages: isImage ? [{ pageNo: 1, imageUrl: previewUrl }] : [],
        recordings: [],
        // Keep direct previews compatible with the existing detail payload resolver.
        meta: { isDirectFilePreview: true },
        isDirectFilePreview: true
      }
    },
    pickDirectFilePreviewPath(item) {
      return this.pickDirectFilePreviewString(item, ['filePath', 'file_path', 'path', 'fileUrl', 'downloadUrl', 'resourceUrl', 'resource_url', 'url', 'src'])
    },
    pickDirectFilePreviewValue(source, keys) {
      if (!source || typeof source != 'object') {
        return undefined
      }
      if (typeof this.pickValue == 'function') {
        return this.pickValue(source, keys)
      }
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i]
        if (source[key] !== undefined && source[key] !== null && source[key] !== '') {
          return source[key]
        }
      }
      return undefined
    },
    pickDirectFilePreviewString(source, keys) {
      if (!source || typeof source != 'object') {
        return ''
      }
      if (typeof this.pickString == 'function') {
        return this.pickString(source, keys)
      }
      const value = this.pickDirectFilePreviewValue(source, keys)
      return value === undefined || value === null ? '' : String(value).trim()
    },
    getDirectFilePreviewExtension(filePath) {
      const cleanPath = String(filePath || '').split(/[?#]/)[0]
      const fileName = cleanPath.split(/[\\/]/).pop() || ''
      const dotIndex = fileName.lastIndexOf('.')
      if (dotIndex < 0 || dotIndex == fileName.length - 1) {
        return ''
      }
      return fileName.slice(dotIndex + 1).toLowerCase()
    },
    getDirectFilePreviewFileName(filePath) {
      const cleanPath = String(filePath || '').split(/[?#]/)[0]
      return cleanPath.split(/[\\/]/).pop() || ''
    },
    toDirectFilePreviewUrl(filePath) {
      const value = String(filePath || '').trim()
      if (!value) {
        return ''
      }
      if (/^(?:blob:|data:|https?:|file:)/i.test(value)) {
        return value
      }
      const normalized = value.replace(/\\/g, '/')
      const encoded = this.encodeDirectFilePreviewPath(normalized)
      if (/^[a-zA-Z]:\//.test(normalized)) {
        return `file:///${encoded}`
      }
      if (normalized.startsWith('//')) {
        return `file:${encoded}`
      }
      if (normalized.startsWith('/')) {
        return `file://${encoded}`
      }
      return encoded
    },
    encodeDirectFilePreviewPath(path) {
      return String(path || '').split('/').map((segment, index) => {
        if (index === 0 && /^[a-zA-Z]:$/.test(segment)) {
          return segment
        }
        return encodeURIComponent(segment)
      }).join('/')
    },
    isResourceCountFolderItem(item) {
      if (!item || typeof item != 'object') {
        return false
      }
      if (item.isFolder) {
        return true
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      const folderFlag = this.pickDirectFilePreviewValue(raw, ['isFolder', 'folder', 'isDir', 'directory'])
      if (folderFlag !== undefined && folderFlag !== null && folderFlag !== '') {
        if (typeof folderFlag == 'boolean') {
          if (folderFlag) {
            return true
          }
        } else {
          const normalizedFlag = String(folderFlag).trim().toLowerCase()
          if (['true', '1', 'folder', 'dir', 'directory', 'yes'].includes(normalizedFlag)) {
            return true
          }
        }
      }
      const resourceType = Number(this.pickDirectFilePreviewValue(raw, ['resourceType', 'resource_type', 'type']) || item.resourceType)
      return Number.isFinite(resourceType) && (resourceType === 0 || resourceType === 5)
    },
    getResourceCountValue(item) {
      if (!item || typeof item != 'object') {
        return null
      }
      const raw = item.raw && typeof item.raw == 'object' ? item.raw : item
      const value = this.pickDirectFilePreviewValue(raw, [
        'childSize',
        'child_size',
        'childrenSize',
        'childCount',
        'childrenCount',
        'pageCount',
        'pages',
        'pageNum',
        'imagePageCount',
        'totalPage'
      ])
      return value === undefined || value === null || value === '' ? null : value
    },
    joinResourceCountUnit(count, unit, language) {
      const text = count === undefined || count === null ? '' : String(count)
      if (!unit) {
        return text
      }
      return COMPACT_COUNT_UNIT_LANGUAGES.includes(String(language || '').trim().toLowerCase())
        ? `${text}${unit}`
        : `${text} ${unit}`
    },
    formatResourceCountLabel(item, options = {}) {
      const emptyText = options.emptyText === undefined ? '' : String(options.emptyText)
      if (!item || typeof item != 'object') {
        return emptyText
      }
      const isFolder = this.isResourceCountFolderItem(item)
      const rawCount = this.getResourceCountValue(item)
      if (rawCount === null) {
        if (isFolder && options.zeroWhenFolder !== false) {
          return this.joinResourceCountUnit(0, options.itemUnit || '', options.language)
        }
        return emptyText
      }
      const numericCount = Number(rawCount)
      const countText = Number.isFinite(numericCount)
        ? String(Math.max(0, numericCount))
        : String(rawCount)
      return this.joinResourceCountUnit(
        countText,
        isFolder ? (options.itemUnit || '') : (options.pageUnit || ''),
        options.language
      )
    }
  }
}
